/**
 * Helpers for summarising a submitted form per top-level section
 * (used by the section navigator in the reviewer/clarification views).
 */

function collectAnswerIds(question, acc) {
  if (!question) return;

  if (question.answerId != null) {
    acc.add(question.answerId);
  }

  const followUps = [
    question.selectedOption?.followUpQuestion,
    ...(question.selectedOptions || []).map((opt) => opt?.followUpQuestion),
    ...(question.options || []).map((opt) => opt?.followUpQuestion),
  ].filter(Boolean);

  followUps.forEach((followUp) => collectAnswerIds(followUp, acc));
}

function collectSectionAnswerIds(section, acc) {
  (section?.questions || []).forEach((q) => collectAnswerIds(q, acc));
  (section?.children || []).forEach((child) => collectSectionAnswerIds(child, acc));
}

/**
 * Keep only questions that have at least one comment (recursively), dropping
 * sections that end up empty. Used by the "show only commented" filter.
 */
export function filterSectionsWithComments(sections = [], allComments = []) {
  const comments = Array.isArray(allComments) ? allComments : [];
  const commentedAnswerIds = new Set(
    comments.map((c) => c?.answerId ?? c?.answer_id).filter((id) => id != null)
  );

  const filterSection = (section) => {
    const questions = (section?.questions || []).filter((q) =>
      commentedAnswerIds.has(q?.answerId)
    );
    const children = (section?.children || []).map(filterSection).filter(Boolean);

    if (questions.length === 0 && children.length === 0) return null;
    return { ...section, questions, children };
  };

  return (sections || []).map(filterSection).filter(Boolean);
}

/**
 * One entry per top-level section with the number of review comments in it.
 *
 * @returns {Array<{sectionId, code, title, label, commentCount}>}
 */
export function getSectionCommentSummaries(sections = [], allComments = []) {
  const comments = Array.isArray(allComments) ? allComments : [];

  return (sections || []).map((section) => {
    const answerIds = new Set();
    collectSectionAnswerIds(section, answerIds);

    const commentCount = comments.filter((c) =>
      answerIds.has(c?.answerId ?? c?.answer_id)
    ).length;

    const code = section?.sectionCode || "";
    const title = section?.sectionTitle || "";

    return {
      sectionId: section?.sectionId,
      code,
      title,
      label: [code, title].filter(Boolean).join(" "),
      commentCount,
    };
  });
}

function collectFollowUpQuestionIds(question, acc) {
  const followUps = [
    question?.selectedOption?.followUpQuestion,
    ...(question?.selectedOptions || []).map((opt) => opt?.followUpQuestion),
    ...(question?.options || []).map((opt) => opt?.followUpQuestion),
  ].filter(Boolean);

  followUps.forEach((followUp) => {
    if (followUp.questionId == null || acc.has(followUp.questionId)) return;
    acc.add(followUp.questionId);
    collectFollowUpQuestionIds(followUp, acc);
  });
}

/**
 * Question ids the tester may revise during clarification: every question the
 * reviewer commented on, plus the follow-up questions those questions can
 * trigger (changing an option must let the tester answer what it reveals).
 * Mirrors the server-side rule in SubmissionServiceImpl#editableQuestionIds.
 *
 * @returns {Set<number>}
 */
export function getEditableQuestionIds(sections = [], allComments = []) {
  const comments = Array.isArray(allComments) ? allComments : [];
  const commentedAnswerIds = new Set(
    comments.map((c) => c?.answerId ?? c?.answer_id).filter((id) => id != null)
  );

  const editable = new Set();
  const visited = new Set();

  const visitQuestion = (question) => {
    if (!question || visited.has(question)) return;
    visited.add(question);
    if (question.answerId != null && commentedAnswerIds.has(question.answerId)) {
      editable.add(question.questionId);
      collectFollowUpQuestionIds(question, editable);
    }
    [
      question.selectedOption?.followUpQuestion,
      ...(question.selectedOptions || []).map((opt) => opt?.followUpQuestion),
      ...(question.options || []).map((opt) => opt?.followUpQuestion),
    ]
      .filter(Boolean)
      .forEach(visitQuestion);
  };

  const visitSection = (section) => {
    (section?.questions || []).forEach(visitQuestion);
    (section?.children || []).forEach(visitSection);
  };

  (sections || []).forEach(visitSection);
  return editable;
}
